import React from 'react'
import classname from 'classnames'
import { useTranslation } from 'react-i18next'

import { IconButton } from '@/components'
import { Badge } from '@/components/atoms'
import { useIsScrolled } from '@/hooks'
import { IntervalSummaryData } from './useIntervalSummaryData'

type IntervalSummaryCardProps = {
    summary: IntervalSummaryData
    onClose: () => void
    onMouseEnter?: () => void
    onMouseLeave?: () => void
}

const Header = ({
    isScrolled,
    onClose,
}: {
    isScrolled: boolean
    onClose: () => void
}) => {
    const { t } = useTranslation('translations')
    return (
        <div
            data-testid="summary-dropdown-content-header"
            className={classname('px-4', {
                'border-b border-b-neutral-200': isScrolled,
            })}
        >
            <div className="mb-1 flex items-center justify-between gap-2">
                <h3
                    data-testid="summary-dropdown-content-header-title"
                    className="text-left font-raleway text-sm font-bold capitalize leading-6 text-regular"
                >
                    {t('active_management.interval.summary.title')}
                </h3>
                <IconButton
                    icon="close"
                    size="medium"
                    name="summary-dropdown-content-header-close-button"
                    variant="ghostSecondary"
                    onClick={onClose}
                />
            </div>
        </div>
    )
}

const MetricRow = ({ summary }: { summary: IntervalSummaryData }) => {
    const { t } = useTranslation('translations')
    const hasContext = Boolean(summary.contextMeterName)

    return (
        <li
            data-testid="interval-summary-row-metric"
            className={classname(
                'grid items-center gap-x-4 font-inter',
                hasContext
                    ? 'grid-cols-[minmax(0,1fr)_auto_auto]'
                    : 'grid-cols-[minmax(0,1fr)_auto]'
            )}
        >
            {summary.metricLabelKey && (
                <span className="min-w-0 text-xs font-medium leading-4 text-subtle">
                    {t(summary.metricLabelKey)}
                </span>
            )}

            {hasContext && (
                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-medium leading-4 text-subtle">
                    <span
                        className="h-2 w-2 rounded-full"
                        aria-hidden="true"
                        style={{
                            backgroundColor:
                                summary.contextMeterColor ?? 'bg-neutral-300',
                        }}
                    />
                    {t('active_management.interval.summary.meter_label', {
                        n: summary.contextMeterName,
                    })}
                </span>
            )}

            <span className="justify-self-end whitespace-nowrap text-right text-xs font-semibold leading-4 text-regular">
                {summary.hasData
                    ? summary.metricValue
                    : t('active_management.interval.summary.no_data')}
            </span>
        </li>
    )
}

const CompletenessRow = ({ summary }: { summary: IntervalSummaryData }) => {
    const { t } = useTranslation('translations')
    return (
        <li
            data-testid="interval-summary-row-completeness"
            className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 font-inter"
        >
            <span className="min-w-0 text-xs font-medium leading-4 text-subtle">
                {t(
                    'active_management.interval.summary.minimum_interval_completeness'
                )}
            </span>

            <span className="whitespace-nowrap text-xs font-semibold leading-4 text-regular">
                {t('active_management.interval.summary.completeness', {
                    actual: summary.minimumReportedMeters,
                    expected: summary.totalMeters,
                })}
            </span>

            <Badge>{summary.completenessPercentage}%</Badge>
        </li>
    )
}

export const IntervalSummaryCard: React.FC<IntervalSummaryCardProps> = ({
    summary,
    onClose,
    onMouseEnter,
    onMouseLeave,
}) => {
    const scrollableRef = React.useRef<HTMLUListElement>(null)
    const isScrolled = useIsScrolled(scrollableRef)

    return (
        <div
            data-testid="summary-dropdown-content"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            role="tooltip"
        >
            <Header isScrolled={isScrolled} onClose={onClose} />

            <ul
                ref={scrollableRef}
                data-testid="interval-summary-list"
                className="flex flex-col gap-2 px-4 pb-1 pt-2"
            >
                <MetricRow summary={summary} />
                <li aria-hidden="true" className="h-px w-full bg-neutral-200" />
                <CompletenessRow summary={summary} />
            </ul>
        </div>
    )
}
